// ════════════════════════════════════════════════════════════════════════════
// TarefasPageAgenda — r140
// Visão "Agenda": layout 2 blocos (lista compacta de tarefas | calendário).
// Drag&drop pra planejar/apontar tarefas no calendário.
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import tarefaService from '../../services/tarefa.service';
import apontamentoService from '../../services/apontamento.service';
import agendaService from '../../services/agenda.service';
import BrowseHeader from '../../components/BrowseHeader';
import BtnIcone from '../../components/BtnIcone';
import SePode from '../../components/SePode';
import TarefaModal from './TarefaModal';
import AgendaSemanal from '../../components/AgendaSemanal';
import { IconeAbaLista, IconeAbaAgenda } from '../../components/SelectOrdemTarefas';   // r220
import CardTarefaCompacto from '../../components/CardTarefaCompacto';
import NovoBlocoModal from '../../components/NovoBlocoModal';
import BlocoAcaoModal from '../../components/BlocoAcaoModal';   // r147
import ApontamentoModal from './ApontamentoModal';   // r175: marcar executada (descrição + faturável)
import ConflitoModal from '../../components/ConflitoModal';      // r147
import TarefasFiltrosBar, { FILTROS_DEFAULT as FILTROS_TAR_DEFAULT, normalizarFiltros } from '../../components/TarefasFiltrosBar';  // r154
import PopupNotificacoesTarefas, { useAlertaNotificacoes }                    // r186
  from '../../components/Notificacoes/PopupNotificacoesTarefas';

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',
  verde:   '#1D9E75', verdeBg: '#f0fff4',
  vermelho:'#e53935', vermBg:  '#fff0f0',
  amarelo: '#d97706', amlBg:   '#fffbeb',
  cinzaBg: '#fafbfc',
};

function inicioDaSemana(d) {
  const dt = new Date(d); dt.setHours(0, 0, 0, 0);
  const dia = dt.getDay();
  dt.setDate(dt.getDate() + (dia === 0 ? -6 : 1 - dia));
  return dt;
}
function addDias(d, n) { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt; }

function TarefasPageAgenda({ onAlternarModo }) {
  const { usuario } = useAuth();

  // ── Tarefas (esquerda) ─────────────────────────────────────────────────
  const [tarefas, setTarefas]   = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [painel, setPainel] = useState(null);          // pra TarefaModal
  const [selecionada, setSelecionada] = useState(null);

  // r186: mesmo alerta da visão Lista — a tela é a mesma pro usuário.
  const alerta = useAlertaNotificacoes(usuario);
  const { verificar: verificarNotificacoes } = alerta;

  // r154: filtros completos das tarefas (mesmos da Lista), com persistência
  const STORAGE_KEY_FILTROS = (uid) => `tarefas.filtros.agenda.${uid || 'anon'}`;
  const [filtrosTar, setFiltrosTar] = useState(() => {
    if (!usuario?.id) return FILTROS_TAR_DEFAULT;
    try {
      const raw = localStorage.getItem(STORAGE_KEY_FILTROS(usuario.id));
      if (raw) return normalizarFiltros({ ...FILTROS_TAR_DEFAULT, ...JSON.parse(raw) });
    } catch {}
    return FILTROS_TAR_DEFAULT;
  });
  // Persiste filtros
  useEffect(() => {
    if (!usuario?.id) return;
    try {
      localStorage.setItem(STORAGE_KEY_FILTROS(usuario.id), JSON.stringify(filtrosTar));
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosTar, usuario]);

  // ── Calendário (direita) ───────────────────────────────────────────────
  const [alocacoes, setAlocacoes] = useState([]);
  const [carregandoAg, setCarregandoAg] = useState(true);
  const [visao, setVisao] = useState('util');
  const [dataAtual, setDataAtual] = useState(new Date());
  // r154: renomeado pra filtrosCal pra não confundir com filtrosTar (da lista)
  const [filtrosCal, setFiltrosCal] = useState({
    planejadas: true, finalizadas: true, outros: true,
  });

  // ── Modal de drop ──────────────────────────────────────────────────────
  const [dropPendente, setDropPendente] = useState(null); // { tarefa, slotInicio }
  const [salvandoBloco, setSalvandoBloco] = useState(false);

  // r147 — Modal de ações do bloco (substitui window.prompt)
  const [blocoAcao, setBlocoAcao] = useState(null);          // aloc clicada
  const [processandoBloco, setProcessandoBloco] = useState(false);
  const [execModal, setExecModal] = useState(null);   // r175: { tarefaId, dadosIniciais, blocoPlanejadoId }

  // r147 — Modal de conflito (substitui window.confirm)
  const [conflitoModal, setConflitoModal] = useState(null);
  //  { conflitos, mensagem, onConfirmar (callback que refaz a ação com force=true) }

  // r154: Carregar tarefas com todos os filtros (papel, status, setor, etc)
  const carregarTarefas = useCallback(async () => {
    if (!usuario?.id) return;
    try {
      setCarregando(true);
      const params = { usuario_id: usuario.id };

      // r171: recorte de papel resolvido no backend (ANDado com o perfil de
      // acesso). O antigo 'excluir_responsavel_id' nem existia lá — era no-op.
      params.papel = filtrosTar.papel;

      // Multi-valor → CSV
      if (filtrosTar.status_execucao.length > 0) params.status_execucao = filtrosTar.status_execucao.join(',');
      if (filtrosTar.setor.length > 0)            params.setor           = filtrosTar.setor.join(',');
      if (filtrosTar.prioridade.length > 0)       params.prioridade      = filtrosTar.prioridade.join(',');
      if (filtrosTar.cliente_ids.length > 0)      params.cliente_ids     = filtrosTar.cliente_ids.join(',');
      if (filtrosTar.negocio_ids.length > 0)      params.negocio_ids     = filtrosTar.negocio_ids.join(',');
      if (filtrosTar.solicitante_ids.length > 0)  params.solicitante_ids = filtrosTar.solicitante_ids.join(',');
      if (filtrosTar.responsavel_ids.length > 0)  params.responsavel_ids = filtrosTar.responsavel_ids.join(',');

      // Datas
      if (filtrosTar.data_prog_de)   params.data_prog_de   = filtrosTar.data_prog_de;
      if (filtrosTar.data_prog_ate)  params.data_prog_ate  = filtrosTar.data_prog_ate;
      if (filtrosTar.deadline_de)    params.deadline_de    = filtrosTar.deadline_de;
      if (filtrosTar.deadline_ate)   params.deadline_ate   = filtrosTar.deadline_ate;

      // Busca
      if (filtrosTar.busca.trim())   params.busca = filtrosTar.busca.trim();

      const lista = await tarefaService.listar(params);
      setTarefas(lista || []);
    } catch (err) {
      console.error(err);
      setTarefas([]);
    } finally {
      setCarregando(false);
    }
  }, [usuario, filtrosTar]);

  // ─── Carregar alocações da semana ──────────────────────────────────────
  const carregarAgenda = useCallback(async () => {
    if (!usuario?.id) return;
    try {
      setCarregandoAg(true);
      const ini = inicioDaSemana(dataAtual);
      const fim = addDias(ini, 7);
      const lista = await agendaService.listarMinhasAlocacoes(
        usuario.id,
        ini.toISOString(),
        fim.toISOString(),
      );
      setAlocacoes(lista || []);
    } catch (err) {
      console.error(err);
      setAlocacoes([]);
    } finally {
      setCarregandoAg(false);
    }
  }, [usuario, dataAtual]);

  useEffect(() => { carregarTarefas(); }, [carregarTarefas]);
  useEffect(() => { carregarAgenda(); }, [carregarAgenda]);

  // r146: separar planejadas e apontadas em sets distintos pra mostrar pílula no card
  const { idsPlanejados, idsApontados } = useMemo(() => {
    const planejados = new Set();
    const apontados  = new Set();
    (alocacoes || []).forEach(a => {
      if (!a.tarefa_id) return;
      if (a.tipo === 'TAREFA_PLANEJADA') planejados.add(a.tarefa_id);
      if (a.tipo === 'TAREFA')           apontados.add(a.tarefa_id);
    });
    return { idsPlanejados: planejados, idsApontados: apontados };
  }, [alocacoes]);

  // r154: backend já filtrou tudo (papel, status, setor, busca, etc).
  //       Frontend só passa adiante — sem filtragem local.
  const tarefasFiltradas = tarefas;

  // ─── Drop: arrastou tarefa no calendário ───────────────────────────────
  const aoDropTarefa = (tarefaId, slotInicio) => {
    const tarefa = tarefas.find(t => t.id === tarefaId);
    if (!tarefa) return;
    setDropPendente({ tarefa, slotInicio });
  };

  // r145: helper — após apontar (passado), se tarefa estava "Não Iniciada",
  //               passa pra "Em Andamento" automaticamente
  const garantirEmAndamento = async (tarefa) => {
    if (tarefa?.status_execucao === 'N') {
      try {
        await tarefaService.alterarStatusExecucao(tarefa.id, 'E', usuario.id, false);
      } catch (err) {
        console.error('Não foi possível mudar status pra Em Andamento:', err);
        // não bloqueia o fluxo — apontamento já está salvo
      }
    }
  };

  // ─── Confirma o bloco ──────────────────────────────────────────────────
  // ─── Helper: executa a ação de salvar bloco (com ou sem force) ──────────
  const salvarBlocoComForce = async ({ dtInicio, dtFim, ehFuturo, descricao, faturavel, force, tarefa }) => {
    const dataIni = new Date(dtInicio).toISOString();
    const dataFim = new Date(dtFim).toISOString();
    if (ehFuturo) {
      await agendaService.planejarTarefa({
        tarefa_id:   tarefa.id,
        data_inicio: dataIni,
        data_fim:    dataFim,
        usuario_id:  usuario.id,
        force:       !!force,
      });
    } else {
      await apontamentoService.incluir(
        tarefa.id,
        { data_inicio: dataIni, data_fim: dataFim, descricao, faturavel },   // r175
        usuario.id,
        !!force,
      );
      await garantirEmAndamento(tarefa);   // r145
    }
  };

  const confirmarBloco = async ({ dtInicio, dtFim, ehFuturo, descricao, faturavel }) => {
    if (!dropPendente) return;
    const { tarefa } = dropPendente;
    try {
      setSalvandoBloco(true);
      await salvarBlocoComForce({ dtInicio, dtFim, ehFuturo, descricao, faturavel, force: false, tarefa });
      setDropPendente(null);
      await carregarAgenda();
      await carregarTarefas();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409 && err.response.data?.requer_confirmacao) {
        // r147: abre modal de conflito bonito (era window.confirm)
        setConflitoModal({
          conflitos: err.response.data.conflitos || [],
          mensagem:  err.response.data.mensagem,
          onConfirmar: async () => {
            try {
              await salvarBlocoComForce({ dtInicio, dtFim, ehFuturo, descricao, faturavel, force: true, tarefa });
              setConflitoModal(null);
              setDropPendente(null);
              await carregarAgenda();
              await carregarTarefas();
            } catch (err2) {
              console.error('Erro detalhado:', err2);
              alert('Erro ao salvar: ' + (err2.response?.data?.mensagem || err2.message || 'desconhecido'));
              setConflitoModal(null);
            }
          },
        });
      } else {
        console.error('Erro detalhado:', err);
        alert('Erro ao salvar: ' + (err.response?.data?.mensagem || err.message || 'desconhecido'));
      }
    } finally {
      setSalvandoBloco(false);
    }
  };

  // ─── Click em bloco do calendário ──────────────────────────────────────
  // r147: abre o BlocoAcaoModal bonito (substituiu window.prompt).
  const aoClicarBloco = (aloc) => {
    setBlocoAcao(aloc);
  };

  // r175: "Marcar como Executada" abre o ApontamentoModal (descrição + faturável).
  //       Ao salvar, cancela o bloco planejado (via onSalvar do modal).
  const acaoMarcarExecutada = () => {
    if (!blocoAcao || !blocoAcao.tarefa_id) return;
    setExecModal({
      tarefaId:         blocoAcao.tarefa_id,
      dadosIniciais:    { data_inicio: blocoAcao.data_inicio, data_fim: blocoAcao.data_fim },
      blocoPlanejadoId: blocoAcao.id,
    });
    setBlocoAcao(null);
  };

  // r175: chamado pelo ApontamentoModal após criar o apontamento (executado).
  //       Cancela a planejada e atualiza status/agenda.
  const aoExecutarConcluido = async () => {
    const ctx = execModal;
    setExecModal(null);
    try {
      if (ctx?.blocoPlanejadoId) await agendaService.cancelar(ctx.blocoPlanejadoId, usuario.id);
      const t = tarefas.find(x => x.id === ctx?.tarefaId)
             || (ctx?.tarefaId ? await tarefaService.buscarPorId(ctx.tarefaId) : null);
      if (t) await garantirEmAndamento(t);
    } catch (err) {
      console.warn('Falha ao finalizar marcação de executada:', err.message);
    }
    await carregarAgenda();
    await carregarTarefas();
  };

  const acaoCancelarPlanejamento = async () => {
    if (!blocoAcao) return;
    if (!window.confirm('Cancelar este planejamento? O bloco será removido do calendário.')) return;
    setProcessandoBloco(true);
    try {
      await agendaService.cancelar(blocoAcao.id, usuario.id);
      setBlocoAcao(null);
      await carregarAgenda();
      await carregarTarefas();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setProcessandoBloco(false);
    }
  };

  const acaoAbrirTarefa = async () => {
    if (!blocoAcao?.tarefa_id) return;
    try {
      const t = await tarefaService.buscarPorId(blocoAcao.tarefa_id);
      setSelecionada(t);
      setPainel('visualizar');
      setBlocoAcao(null);
      verificarNotificacoes();
    } catch (err) {
      alert('Erro ao abrir tarefa: ' + (err.response?.data?.mensagem || err.message));
    }
  };

  // r148/149: chamado quando usuário move ou redimensiona um bloco
  //           (planejada → agendaService.alterar / apontamento → apontamentoService.alterar)
  const aoAlterarBloco = async (aloc, dataInicio, dataFim) => {
    const ehPlanejada = aloc.tipo === 'TAREFA_PLANEJADA';
    const ehApontamento = aloc.tipo === 'TAREFA';
    if (!ehPlanejada && !ehApontamento) return;   // outros tipos: ignora

    const chamarAlterar = async (force) => {
      if (ehPlanejada) {
        await agendaService.alterar(aloc.id, {
          data_inicio: dataInicio, data_fim: dataFim,
          usuario_id: usuario.id, force,
        });
      } else {
        // Apontamento — precisa do apontamento_id
        if (!aloc.apontamento_id) {
          throw new Error('Apontamento sem ID — não dá pra alterar. Tenta recarregar a página.');
        }
        await apontamentoService.alterar(
          aloc.apontamento_id,
          { data_inicio: dataInicio, data_fim: dataFim,
            descricao: aloc.observacao || undefined },
          usuario.id,
          force,
        );
      }
    };

    try {
      await chamarAlterar(false);
      await carregarAgenda();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409 && err.response.data?.requer_confirmacao) {
        setConflitoModal({
          conflitos: err.response.data.conflitos || [],
          mensagem:  err.response.data.mensagem,
          onConfirmar: async () => {
            try {
              await chamarAlterar(true);
              setConflitoModal(null);
              await carregarAgenda();
            } catch (err2) {
              alert('Erro ao alterar: ' + (err2.response?.data?.mensagem || err2.message));
              setConflitoModal(null);
              await carregarAgenda();
            }
          },
        });
      } else {
        alert('Erro ao alterar: ' + (err.response?.data?.mensagem || err.message));
        await carregarAgenda();
      }
    }
  };

  // r149: excluir apontamento
  const acaoExcluirApontamento = async () => {
    if (!blocoAcao || blocoAcao.tipo !== 'TAREFA') return;
    if (!blocoAcao.apontamento_id) {
      alert('Apontamento sem ID — não dá pra excluir. Tenta recarregar a página.');
      return;
    }
    if (!window.confirm('Excluir este apontamento? As horas serão removidas da tarefa.')) return;
    setProcessandoBloco(true);
    try {
      await apontamentoService.excluir(blocoAcao.apontamento_id, usuario.id);
      setBlocoAcao(null);
      await carregarAgenda();
      await carregarTarefas();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.mensagem || err.message));
    } finally {
      setProcessandoBloco(false);
    }
  };

  // ─── Abrir tarefa pra ver/editar ───────────────────────────────────────
  const abrirTarefa = async (t) => {
    const cheia = await tarefaService.buscarPorId(t.id);
    setSelecionada(cheia);
    setPainel('visualizar');
    verificarNotificacoes();
  };

  // r186: abre pelo ID (usado pelo pop-up de notificações)
  const abrirTarefaPorId = async (id) => {
    if (!id) return;
    try {
      const cheia = await tarefaService.buscarPorId(id, usuario?.id);
      if (cheia) { setSelecionada(cheia); setPainel('visualizar'); }
    } catch (err) {
      alert(err.response?.data?.mensagem || `Não foi possível abrir a tarefa #${id}.`);
    }
  };

  const aoSalvar = async () => {
    setPainel(null);
    setSelecionada(null);
    await carregarTarefas();
    await carregarAgenda();
    verificarNotificacoes();
  };

  return (
    <div style={{ fontFamily: "'Manrope', sans-serif" }}>
      {/* Header padronizado */}
      <BrowseHeader
        rotina="tarefas"
        titulo="Tarefas e Pendências"
        descricao="Arraste uma tarefa pro calendário pra planejar (futuro) ou apontar (passado)."
      />

      {/* Barra superior — alternar Lista/Agenda */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        marginBottom: '10px', padding: '8px 10px',
        backgroundColor: COR.branco,
        border: `1px solid ${COR.borda}`, borderRadius: '8px',
      }}>
        <div style={{ display: 'flex', gap: '0', borderRadius: '5px', overflow: 'hidden', border: `1px solid ${COR.borda}` }}>
          {/* r220: ícones das abas em SVG (eram emoji 📋/📅) */}
          <button onClick={() => onAlternarModo('lista')} style={{
            padding: '6px 14px', border: 'none', background: COR.branco, color: COR.muted,
            fontSize: '12px', fontWeight: '500', cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif",
            display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}>
            <IconeAbaLista tamanho={14} cor={COR.muted} />
            Lista
          </button>
          <button style={{
            padding: '6px 14px', border: 'none', background: COR.azul, color: COR.branco,
            fontSize: '12px', fontWeight: '600', cursor: 'pointer',
            fontFamily: "'Manrope', sans-serif",
            display: 'inline-flex', alignItems: 'center', gap: '6px',
          }}>
            <IconeAbaAgenda tamanho={14} cor={COR.branco} />
            Agenda
          </button>
        </div>
        <span style={{ fontSize: '11px', color: COR.muted, marginLeft: '8px', fontStyle: 'italic' }}>
          Arraste cards pro calendário pra planejar ou apontar horas
        </span>
      </div>

      {/* r154: Barra de filtros completos (mesmos da Lista) */}
      <TarefasFiltrosBar
        filtros={filtrosTar}
        setFiltros={setFiltrosTar}
        tarefas={tarefas}
        showActions={true}
        onNovaTarefa={() => { setSelecionada(null); setPainel('novo'); }}
        totalRegistros={tarefasFiltradas.length}
      />

      {/* Layout 2 colunas — r146: 35% / 65% */}
      <div style={{ display: 'grid', gridTemplateColumns: '35% 65%', gap: '10px' }}>

        {/* ────────── BLOCO ESQUERDO: tarefas compactas ────────── */}
        <div style={{
          backgroundColor: COR.branco,
          border: `1px solid ${COR.borda}`, borderRadius: '8px',
          padding: '10px', display: 'flex', flexDirection: 'column',
          maxHeight: 'calc(100vh - 220px)',
        }}>
          <div style={{ overflowY: 'auto', flex: 1, paddingRight: '4px' }}>
            {carregando ? (
              <div style={{ textAlign: 'center', padding: '20px', color: COR.muted, fontSize: '12px' }}>
                Carregando...
              </div>
            ) : tarefasFiltradas.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', color: COR.muted, fontSize: '12px', fontStyle: 'italic' }}>
                Nenhuma tarefa pendente.
              </div>
            ) : tarefasFiltradas.map(t => (
              <CardTarefaCompacto
                key={t.id}
                tarefa={t}
                onClick={abrirTarefa}
                temPlanejada={idsPlanejados.has(t.id)}
                temApontamento={idsApontados.has(t.id)}
              />
            ))}
          </div>
        </div>

        {/* ────────── BLOCO DIREITO: calendário ────────── */}
        <div>
          <AgendaSemanal
            alocacoes={alocacoes}
            visao={visao}    setVisao={setVisao}
            dataAtual={dataAtual} setDataAtual={setDataAtual}
            filtros={filtrosCal}  setFiltros={setFiltrosCal}
            onDropTarefa={aoDropTarefa}
            onClickBloco={aoClicarBloco}
            onAlterarBloco={aoAlterarBloco}
          />
          {carregandoAg && (
            <div style={{ position: 'absolute', top: '180px', right: '40px',
                          fontSize: '11px', color: COR.muted, fontStyle: 'italic' }}>
              Atualizando agenda...
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmação do drop */}
      <NovoBlocoModal
        aberto={!!dropPendente}
        tarefa={dropPendente?.tarefa}
        slotInicio={dropPendente?.slotInicio}
        onConfirmar={confirmarBloco}
        onCancelar={() => setDropPendente(null)}
        salvando={salvandoBloco}
      />

      {/* r147: Modal de ações do bloco clicado */}
      <BlocoAcaoModal
        aberto={!!blocoAcao}
        aloc={blocoAcao}
        onMarcarExecutada={acaoMarcarExecutada}
        onCancelarPlanejamento={acaoCancelarPlanejamento}
        onAbrirTarefa={acaoAbrirTarefa}
        onExcluirApontamento={acaoExcluirApontamento}
        onFechar={() => setBlocoAcao(null)}
        processando={processandoBloco}
      />

      {/* r175: Modal de apontamento pra "Marcar como Executada" (descrição + faturável) */}
      {execModal && (
        <ApontamentoModal
          aberto={true}
          tarefaId={execModal.tarefaId}
          dadosIniciais={execModal.dadosIniciais}
          apontarComForce={true}
          onFechar={() => setExecModal(null)}
          onSalvar={aoExecutarConcluido}
        />
      )}

      {/* r147: Modal de conflito de horários */}
      <ConflitoModal
        aberto={!!conflitoModal}
        conflitos={conflitoModal?.conflitos}
        mensagem={conflitoModal?.mensagem}
        onConfirmar={() => conflitoModal?.onConfirmar?.()}
        onCancelar={() => setConflitoModal(null)}
        processando={salvandoBloco}
      />

      {/* Modal de tarefa (visualizar/editar) */}
      {painel && (
        <TarefaModal
          aberto={!!painel}
          modo={painel}
          tarefa={selecionada}
          onFechar={() => { setPainel(null); setSelecionada(null); }}
          onSalvar={aoSalvar}
          onAtualizar={async () => { await carregarTarefas(); await carregarAgenda(); }}
          onTrocarModoEditar={() => setPainel('editar')}
        />
      )}

      {/* r186: alerta de notificações não lidas */}
      <PopupNotificacoesTarefas alerta={alerta} onAbrirTarefa={abrirTarefaPorId} />
    </div>
  );
}

export default TarefasPageAgenda;
